import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { KeyRound, AlertTriangle } from "lucide-react";

export default function Recovery() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/use", { email, code });
      setToken(data.token);
      // Seed last-activity so the 30-min idle check doesn't immediately reject us.
      try { localStorage.setItem("elle_last_activity", String(Date.now())); } catch (_e) { /* ignore */ }
      // Re-run bootstrap so AuthProvider picks up the new token + author,
      // otherwise Protected routes will bounce us to /login.
      await refresh();
      setResult(data);
      toast.success("Access restored. Please save your new 2FA secret.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recovery failed.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="recovery-success">
        <div className="elle-surface p-8 rounded-sm max-w-lg w-full fade-up"
             style={{ borderColor: "var(--elle-amber)" }}>
          <div className="overline" style={{ color: "var(--elle-amber-bright)" }}>
            Access restored
          </div>
          <h2 className="font-display text-3xl mt-2">Save your new 2FA secret.</h2>
          <p className="text-sm mt-3" style={{ color: "var(--elle-ink-dim)" }}>
            Your old 2FA device has been revoked. Add this new secret to your authenticator app now —
            you'll need it for every future Tier B change.
          </p>
          <div className="elle-surface-2 p-5 rounded-sm my-5">
            <div className="overline mb-1">New TOTP secret</div>
            <div className="font-mono-elle text-lg tracking-widest break-all"
                 data-testid="recovery-new-totp"
                 style={{ color: "var(--elle-amber)" }}>
              {result.new_totp_secret}
            </div>
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--elle-ink-faint)" }}>
            You should also change your password from Settings once you're in.
          </p>
          <Button data-testid="recovery-continue" onClick={() => nav("/dashboard/settings")}
                  className="btn-amber rounded-sm w-full h-11">
            Continue to the study
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="recovery-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up">
        <div>
          <div className="flex items-center gap-2 overline" style={{ color: "var(--elle-oxblood-bright)" }}>
            <KeyRound size={14}/> Account recovery
          </div>
          <h2 className="font-display text-4xl mt-2">Lost access?</h2>
          <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Enter a one-time recovery code. Your 2FA will be rotated on success.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2">Email</label>
            <Input data-testid="recovery-email" type="email" required
                   value={email} onChange={(e)=>setEmail(e.target.value)}/>
          </div>
          <div>
            <label className="overline block mb-2">Recovery code</label>
            <Input data-testid="recovery-code" required
                   value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())}
                   className="font-mono-elle tracking-widest"
                   placeholder="XXXXX-XXXXX"/>
          </div>
        </div>
        <div className="flex items-start gap-2 text-xs"
             style={{ color: "var(--elle-ink-faint)" }}>
          <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
          <span>
            Each code is single-use. Using one rotates your 2FA secret.
          </span>
        </div>
        <Button data-testid="recovery-submit" type="submit" disabled={busy}
                className="btn-oxblood rounded-sm w-full h-11">
          {busy ? "Recovering…" : "Recover access"}
        </Button>
        <Link to="/login" className="block text-center text-xs underline"
              style={{ color: "var(--elle-ink-dim)" }}>
          Back to sign-in
        </Link>
      </form>
    </div>
  );
}
